import React, { useMemo, useCallback } from 'react';
import { Typography, makeStyles, createStyles } from '@material-ui/core';
import { parseISO } from 'date-fns';
import {
  DataTable,
  IDataTableColumn,
  DateDisplay,
  EnumValueDisplay,
  ShowMoreButton,
  ButtonLink,
} from '@lib/common';
import { PaginationState } from '@lib/state';
import { BulkChangeBillingRun } from 'app/state';

interface Props {
  runs: BulkChangeBillingRun[];
  pagination?: PaginationState;
  onLoadMore: () => void;
}

const useStyles = makeStyles(() =>
  createStyles({
    tableCell: {
      whiteSpace: 'nowrap',
    },
  })
);

export const BulkChangeBillingRunsList: React.FC<Props> = ({ runs, pagination, onLoadMore }) => {
  const styles = useStyles();

  const renderCell = useCallback(
    (data?: string | number | null) => (
      <Typography className={styles.tableCell}>{data ?? '—'}</Typography>
    ),
    [styles]
  );

  const columns: IDataTableColumn<BulkChangeBillingRun>[] = useMemo(
    () => [
      {
        title: 'Status',
        valueFactory: run => <EnumValueDisplay className={styles.tableCell} value={run.status} />,
      },
      {
        title: 'Corporate Account',
        valueFactory: run => renderCell(run.corporateAccountName),
      },
      {
        title: 'Created',
        valueFactory: run => (run.createdAt ? <DateDisplay date={parseISO(run.createdAt)} /> : '—'),
      },
      {
        title: 'Completed',
        valueFactory: run =>
          run.completedAt ? <DateDisplay date={parseISO(run.completedAt)} /> : '—',
      },
      {
        title: 'Requested',
        valueFactory: run => renderCell(run.totalRequested),
      },
      {
        title: 'Succeeded',
        valueFactory: run => renderCell(run.totalSucceeded),
      },
      {
        title: 'Failed',
        valueFactory: run => renderCell(run.totalFailed),
      },
      {
        title: 'View',
        valueFactory: run => (
          <ButtonLink to={`/bulk-change-billing/${run.id}`} color="primary" variant="text">
            View Run Details
          </ButtonLink>
        ),
      },
    ],
    [styles, renderCell]
  );

  return (
    <DataTable items={runs} columns={columns} getItemKey={run => run.id ?? ''}>
      <ShowMoreButton isDone={pagination?.isDone ?? true} loadMore={onLoadMore} />
    </DataTable>
  );
};
